import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { getSalesOrderInboxEmail, getSalesOrderInboxEmailMeta } from '@/services/api'
import type { AttachmentReference } from '@/types/orderConfirmation'
import { EmailBody } from '@/components/EmailBody'
import { AttachmentSection } from '@/features/order-confirmation/components/OcSource'
import { cn } from '@/lib/utils'
import { EmailMetaPanel } from '@/features/order-confirmation/inbox/EmailMetaPanel'
import { OutlookFolderBadge } from '@/features/order-confirmation/inbox/OutlookFolderBadge'
import {
  salesOrderDetailQueryKeys,
  salesOrderQueryOptions,
} from '@/features/sales-order/salesOrderQueries'

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleString('sv-SE', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

/**
 * An attachment on its own. The order confirmation inbox puts a link to the matched purchase order
 * under each attachment; there is no such link on this side, because a customer order is reached
 * from the sales order overview rather than from the mail it arrived in.
 */
function AttachmentCard({ attachment }: { attachment: AttachmentReference }) {
  return (
    <Container className="min-w-0">
      <ContainerContent className="space-y-4 p-5">
        <AttachmentSection attachment={attachment} prominent />
      </ContainerContent>
    </Container>
  )
}

export function EmailDetailPage() {
  const { workflowId, emailId } = useParams<{ workflowId: string; emailId: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: salesOrderDetailQueryKeys.inboxEmail(workflowId ?? '', emailId ?? ''),
    queryFn: () => getSalesOrderInboxEmail(workflowId!, emailId!),
    enabled: !!workflowId && !!emailId,
    ...salesOrderQueryOptions,
  })

  const inboxHref = `/sales-order/${workflowId}/inbox`

  if (isLoading) {
    return <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
  }

  if (isError || !data) {
    return (
      <div className="max-w-lg space-y-4">
        <p className="text-sm text-gray-600">Could not load this email.</p>
        <Button type="button" variant="outline" size="sm" asChild>
          <Link to={inboxHref}>Back to inbox</Link>
        </Button>
      </div>
    )
  }

  const attachments = data.attachments ?? []

  return (
    <div className="container mx-auto space-y-6">
      {/* Back */}
      <Button type="button" variant="ghost" size="sm" className="-ml-2 gap-1 text-on-canvas-muted hover:bg-white/40 hover:text-on-canvas" asChild>
        <Link to={inboxHref}>
          <ArrowLeft className="h-4 w-4" />
          Inbox
        </Link>
      </Button>

      <div className={cn(
        'grid grid-cols-1 items-stretch gap-4',
        attachments.length > 0 && 'lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]',
      )}>
        {/* Email */}
        <Container className="min-w-0">
          <ContainerContent className="p-0">
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <h1 className="text-base font-semibold text-gray-900">
                    {data.subject || '(no subject)'}
                  </h1>
                  <p className="text-sm text-gray-600">{data.sender_address}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <OutlookFolderBadge folder={data.outlook_folder} />
                  <p className="text-xs text-gray-400">{formatDateTime(data.received)}</p>
                </div>
              </div>
            </div>
            <div className="max-h-[min(70vh,720px)] overflow-y-auto px-5 py-4">
              {data.body
                ? <EmailBody html={data.body} plainTextClassName="text-sm text-gray-700" />
                : <p className="text-sm italic text-gray-400">(no body)</p>
              }
            </div>
            <div className="border-t border-gray-100 px-5 py-3">
              <EmailMetaPanel
                workflowId={workflowId!}
                emailId={emailId!}
                fetchMeta={getSalesOrderInboxEmailMeta}
                queryKeyPrefix="sales-order-inbox-email-meta"
                product="sales-order"
              />
            </div>
          </ContainerContent>
        </Container>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="min-w-0 space-y-4">
            {attachments.map((a) => (
              <AttachmentCard key={a.id} attachment={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
